export type Currency = "sek" | "usd" | "eur";

/** Countries quoted in their own currency; everyone else pays in EUR. The
 *  nginx marketing cache keys on this too (lib/marketing-cache.ts), so a page
 *  is rendered once per currency rather than once per visitor country. */
export const COUNTRY_CURRENCIES: Readonly<Partial<Record<string, Currency>>> = { SE: "sek", US: "usd" };

export function currencyForCountry(country: string | null): Currency {
  return (country ? COUNTRY_CURRENCIES[country.toUpperCase()] : undefined) ?? "eur";
}

/**
 * Purchasable subscription tiers. 'pro' is the original 500 SEK / $50 / €50
 * plan; 'starter' is the 149 SEK / $14.90 / €14.90 tier added 2026-07 (V241)
 * with ~1/3 of the quota; 'max' is the 2000 SEK / $200 / €200 tier added
 * 2026-09 (V335) with 4x the quota. All stay purchasable side by side —
 * existing subscribers were deliberately NOT migrated off their Stripe price.
 * Above Max sit the levels (V474, 2026-10).
 */
export type SubscriptionPlan = EnvPlan | MaxLevelPlan;

/** The tiers whose Stripe price IDs are in the environment (lib/stripe-env.ts),
 *  cheapest first. */
export const ENV_PLANS = ["starter", "pro", "max"] as const;
export type EnvPlan = (typeof ENV_PLANS)[number];

/**
 * "Squidler Max Level N", 3 to 10 (V474, .plans/prd-self-serve-tiers.md): N ×
 * 1000 SEK / N × $100 / N × €100 a month excluding VAT, with Pro × 2N of
 * quota. Max is, in effect, level 2. Each is a Stripe product made by
 * deploy/bin/ensure-stripe-tiers, its price found by lookup key rather than
 * an env var, and its `plans` row named like the plan.
 */
export const MAX_LEVELS = [3, 4, 5, 6, 7, 8, 9, 10] as const;
export type MaxLevel = (typeof MAX_LEVELS)[number];
export type MaxLevelPlan = `max_level_${MaxLevel}`;

export const maxLevelPlan = (level: MaxLevel): MaxLevelPlan => `max_level_${level}`;

export const MAX_LEVEL_PLANS: readonly MaxLevelPlan[] = MAX_LEVELS.map(maxLevelPlan);

export const isMaxLevelPlan = (plan: SubscriptionPlan): plan is MaxLevelPlan =>
  (MAX_LEVEL_PLANS as readonly string[]).includes(plan);

/** The N of "max_level_N" — the type admits no other shape. */
export const levelOfPlan = (plan: MaxLevelPlan): MaxLevel =>
  Number(plan.slice("max_level_".length)) as MaxLevel;

/**
 * Every tier we SUPPORT — i.e. that a live subscription can be on.
 *
 * 'starter' stays here after being withdrawn from sale, and must NOT be removed
 * while anyone is still on it. `planForStripePrice` (lib/stripe-env.ts) resolves
 * a Stripe price ID by searching this list; drop 'starter' and an existing
 * subscriber's price matches nothing, `resolvePurchasedPlanName` falls back to
 * 'pro', and their next renewal silently moves them onto the Pro plan's quota
 * while they keep paying the Starter price.
 */
export const SUBSCRIPTION_PLANS: readonly SubscriptionPlan[] = [...ENV_PLANS, ...MAX_LEVEL_PLANS];

/**
 * The tiers we currently SELL to the public. Every promotion surface — pricing
 * page, billing page plan chooser, landing price anchor, the onboarding
 * consultant's sticker — iterates this, never SUBSCRIPTION_PLANS.
 *
 * 'starter' was withdrawn 2026-08-26: its daily quota is low enough that
 * subscribers hit the limit repeatedly, which is a worse first experience than
 * not offering the tier at all. It remains fully supported in Stripe and here,
 * so existing subscribers keep their plan, their price and their renewals
 * untouched — we simply stop pointing new customers at it.
 *
 * 'max' went on general sale 2026-09-15 after a hand-picked preview behind a
 * per-browser cookie; that gate is gone. The levels above it followed in
 * 2026-10. Order is the ladder, cheapest first.
 */
export const PURCHASABLE_PLANS: readonly SubscriptionPlan[] = ["pro", "max", ...MAX_LEVEL_PLANS];

/**
 * The tiers to offer a specific viewer: PURCHASABLE_PLANS, plus Starter in
 * front for `starterOffered` (`users.starter_plan_offered`, V296). That is a
 * promise being kept, not a second public tier: it is set by hand for someone
 * we had personally quoted 149 SEK before withdrawing the tier, who would
 * otherwise arrive at /billing and find nothing at that price.
 */
export const purchasablePlansFor = (starterOffered: boolean): readonly SubscriptionPlan[] =>
  starterOffered && !PURCHASABLE_PLANS.includes("starter")
    ? ["starter", ...PURCHASABLE_PLANS]
    : PURCHASABLE_PLANS;

/**
 * The tiers a subscriber on `current` may change to, up or down, cheapest
 * first — every offered tier but their own. Nothing for a plan row that isn't
 * a tier (null: 'champion', 'internal'). The switch itself is
 * lib/plan-change.ts; this half lives here so the client-side chooser can ask
 * the same question without importing the Stripe env.
 */
export const changeTargetsFor = (
  current: SubscriptionPlan | null,
  offered: readonly SubscriptionPlan[],
): SubscriptionPlan[] =>
  current === null ? [] : offered.filter((plan) => plan !== current);

/** Whether `to` is a bigger tier than `from` — SUBSCRIPTION_PLANS is written
 *  cheapest first. */
export const isUpgrade = (from: SubscriptionPlan, to: SubscriptionPlan): boolean =>
  SUBSCRIPTION_PLANS.indexOf(to) > SUBSCRIPTION_PLANS.indexOf(from);

/**
 * Where a prospect too big for Pro is sent. There is no Enterprise tier in
 * Stripe and no plan row behind this — deliberately: we are still working out
 * what to offer, so the honest surface is a conversation, not a fabricated
 * price or a quota nobody has agreed. Becomes a real plan when a deal closes.
 */
export const ENTERPRISE_CONTACT_EMAIL = "mattias@squidler.io";

export const isSubscriptionPlan = (value: unknown): value is SubscriptionPlan =>
  (SUBSCRIPTION_PLANS as readonly unknown[]).includes(value);

/** The plans.name row a purchased tier promotes onto (webhook). 'pro' keeps
 *  the pre-tier row name 'default' — every subscription row and admin tool
 *  already points at it, so renaming it would be churn for zero user benefit.
 *  Every other tier's row is named after the tier. */
export const dbPlanName = (plan: SubscriptionPlan): Exclude<SubscriptionPlan, "pro"> | "default" =>
  plan === "pro" ? "default" : plan;

/**
 * The inverse of `dbPlanName`: which purchasable tier a `plans.name` row is,
 * or null for a row that isn't a tier at all ('trial', 'internal', 'champion',
 * 'alumni').
 *
 * Defined as the inverse rather than open-coded at call sites because the thing
 * that keeps going wrong is a `planName === "starter" ? … : proPrice` ternary
 * that silently quotes the WRONG PRICE to a subscriber of any tier added after
 * it was written. Deriving it from SUBSCRIPTION_PLANS means a new tier can't be
 * left out.
 */
export const subscriptionPlanForDbName = (name: string | null): SubscriptionPlan | null =>
  SUBSCRIPTION_PLANS.find((plan) => dbPlanName(plan) === name) ?? null;

/**
 * The tiers /billing offers a viewer to change to.
 *
 * Pure and separate from the JSX because the bug it encodes was not in the
 * ladder but in the ARGUMENT: keyed on the effective plan row, an account a
 * human had overridden ('champion', 'internal') resolved to no tier and was
 * shown nothing, so its only route to a different tier was the Stripe customer
 * portal — where, on a Stripe account shared with Squidler QA, a subscriber
 * moved himself onto a QA product on 2026-09-18.
 *
 * `purchasedPlanName` is therefore the input: what Stripe bills, which is the
 * same thing the server's `decidePlanChange` derives from the subscription's
 * price. The two halves of one question now ask it the same way, and an
 * override keeps doing its job — it governs the POOL, and the webhook's
 * promotion still honours `plan_overridden_at` when the upgrade lands.
 */
export const changeTargetsForViewer = (
  facts: {
    /** subscriptions.status. */
    status: string;
    /** Set to cancel at period end — resuming belongs in the portal. */
    canceling: boolean;
    /** plans.name behind purchased_plan_id; null when nothing was bought. */
    purchasedPlanName: string | null;
  },
  offered: readonly SubscriptionPlan[],
): SubscriptionPlan[] =>
  facts.status === "active" && !facts.canceling
    ? changeTargetsFor(subscriptionPlanForDbName(facts.purchasedPlanName), offered)
    : [];

export interface PriceDisplay {
  currency: Currency;
  amount: number;
  label: string;
}

// Fixed sticker prices per geo currency, per tier. The FX spread between them
// (€ ≈ $, SEK ≈ 10:1 — e.g. 500 SEK ≈ $52 at 2026 rates) is absorbed as
// per-currency margin — a tier's USD-cent quota is identical regardless of
// billing currency. TOPUP_TIERS below follows the same convention; see its
// comment for the full rationale + revisit triggers.
const PRICES: Record<EnvPlan, Record<Currency, PriceDisplay>> = {
  starter: {
    sek: { currency: "sek", amount: 149, label: "149 SEK" },
    usd: { currency: "usd", amount: 14.9, label: "$14.90" },
    eur: { currency: "eur", amount: 14.9, label: "€14.90" },
  },
  pro: {
    sek: { currency: "sek", amount: 500, label: "500 SEK" },
    usd: { currency: "usd", amount: 50, label: "$50" },
    eur: { currency: "eur", amount: 50, label: "€50" },
  },
  // Round, not charm-priced (2000, never 1999), and deliberately consistent
  // with Pro's round 500 — a 9-ending next to it would read as a markdown on a
  // tier whose whole proposition is more headroom. Holds the same 10:1 SEK peg
  // as every other price here.
  max: {
    sek: { currency: "sek", amount: 2000, label: "2000 SEK" },
    usd: { currency: "usd", amount: 200, label: "$200" },
    eur: { currency: "eur", amount: 200, label: "€200" },
  },
};

/** A level's sticker: N thousand SEK, N hundred USD or EUR — Max's peg. */
const levelPrice = (level: MaxLevel, currency: Currency): PriceDisplay => {
  const amount = currency === "sek" ? level * 1000 : level * 100;
  const label = currency === "sek" ? `${amount} SEK` : currency === "usd" ? `$${amount}` : `€${amount}`;
  return { currency, amount, label };
};

export function priceForCurrency(plan: SubscriptionPlan, currency: Currency): PriceDisplay {
  return isMaxLevelPlan(plan) ? levelPrice(levelOfPlan(plan), currency) : PRICES[plan][currency];
}

/** Every tier's sticker label in one currency — for pages that hand the whole
 *  ladder to a client component. */
export const priceLabelsFor = (currency: Currency): Record<SubscriptionPlan, string> =>
  Object.fromEntries(SUBSCRIPTION_PLANS.map((plan) => [plan, priceForCurrency(plan, currency).label])) as Record<SubscriptionPlan, string>;

/**
 * Tiers whose sticker price EXCLUDES VAT — so the figure above is not what the
 * customer's card is charged, and every surface quoting it must say so.
 *
 * This is not a presentation choice. It mirrors `tax_behavior` on the Stripe
 * Price, which is what actually decides the invoice:
 *
 *   Pro, Starter   tax_behavior = inclusive   €50 sticker  → €50 charged
 *   Max            tax_behavior = exclusive   €200 sticker → €250 charged (25% VAT)
 *
 * The split most likely tracks the audience rather than being an accident: EU
 * consumer-pricing rules require a B2C price to be shown VAT-inclusive, while
 * B2B prices are conventionally quoted excluding it. Pro and Starter sell to
 * individuals; Max sells to teams. On that reading each tier is configured
 * correctly for who buys it.
 *
 * What does NOT follow from that is quoting both kinds of number side by side
 * with nothing to distinguish them, which is what every price surface did until
 * this list existed. A visitor comparing "€50" and "€200" was comparing a
 * final price against a pre-tax one.
 *
 * WHAT MUST STAY IN SYNC. This list has no automatic connection to Stripe: the
 * app never reads a Price object, it renders from the PRICES map above. If a
 * tier's `tax_behavior` is changed in Stripe, or a new tier ships, this list is
 * the thing that has to be updated by hand — and getting it wrong misquotes a
 * price rather than failing, which is the worst shape of bug. A Stripe Price is
 * immutable, so `tax_behavior` can only change by issuing a NEW price; treat
 * "we re-priced a tier" as the trigger to re-check this.
 *
 * Deliberately not phrased as a computed VAT-inclusive figure: the rate depends
 * on the customer's country (and B2B reverse-charge makes it zero), none of
 * which is known when a marketing page renders. "+ VAT" is true everywhere; a
 * number would be true in one country.
 */
export const TAX_EXCLUSIVE_PLANS: readonly SubscriptionPlan[] = ["max", ...MAX_LEVEL_PLANS];

export const priceExcludesTax = (plan: SubscriptionPlan): boolean =>
  TAX_EXCLUSIVE_PLANS.includes(plan);

/** Whether moving between two tiers changes whether the price includes VAT —
 *  which the customer must agree to before the change (lib/plan-change.ts). */
export const needsVatConsent = (from: SubscriptionPlan, to: SubscriptionPlan): boolean =>
  priceExcludesTax(from) !== priceExcludesTax(to);

export const CURRENCIES: readonly Currency[] = ["sek", "usd", "eur"];

// Stripe price-ID resolution lives in lib/stripe-env.ts — the IDs come from
// the startup-validated Stripe env snapshot, never from ad-hoc process.env
// reads. This module stays env-free (it's imported by client components).

/**
 * Pay-as-you-go top-up tiers ($5/$15/$50 — resolved in the trial-end design
 * review; docs/trial-end-experience-plan.md §6). One-time payments use inline
 * Stripe price_data, so no pre-created price IDs are needed.
 *
 * ── Currency model (deliberate decision, 2026-07) ──────────────────────────
 * The whole quota system is denominated in USD cents (LLM providers price in
 * USD → pi-ai cost.total → drains — see apps/runtime/src/budget.ts), so a
 * top-up must GRANT USD cents. What we CHARGE follows the same convention the
 * subscription established (PRICES above): fixed, marketing-round sticker
 * prices per geo currency, with the FX spread silently absorbed as
 * per-currency margin. Concretely: $5, €5, and 50 SEK all grant the same
 * `creditCents = 500`, so "1:1" is exact for USD and approximate (slightly in
 * our favor at 2026 rates: €5 ≈ $5.4, 50 SEK ≈ $5.2) for EUR/SEK. The user
 * never sees the mismatch — quota renders as USD amounts / a percentage
 * meter, never in their local currency.
 *
 * Alternatives we considered and may switch to later:
 *  1. Exact per-currency credit (50 SEK → ~480 cents): more "just", but needs
 *     an FX source, yields ugly balances, and would be INCONSISTENT with the
 *     subscription (which keeps the fixed-sticker convention).
 *  2. Charge USD only: exact, but breaks the geo-currency pattern and pushes
 *     card-FX fees onto the customer.
 *
 * Revisit triggers: (a) fairness complaints from EUR/SEK users, or (b) FX
 * drifting far from the pegs baked in here (EUR≈USD, SEK≈10:1 — the same 10:1
 * peg the subscription uses; if one moves, review both). Drift is auditable
 * from data we already record — every grant stores what was actually paid:
 *
 *   SELECT currency, sum(amount_paid_cents) AS paid, sum(credit_cents) AS granted
 *   FROM topups GROUP BY currency;
 *
 * `creditCents` = USD cents landing in subscriptions.purchased_credit_cents,
 * regardless of charged currency. `unitAmount` = Stripe minor-unit charge per
 * currency.
 */
export type TopupTier = "small" | "medium" | "large";

export interface TopupOption {
  tier: TopupTier;
  creditCents: number;
  label: Record<Currency, string>;
  unitAmount: Record<Currency, number>;
}

export const TOPUP_TIERS: readonly TopupOption[] = [
  { tier: "small",  creditCents: 500,  label: { usd: "$5",  eur: "€5",  sek: "50 SEK" },  unitAmount: { usd: 500,  eur: 500,  sek: 5000 } },
  { tier: "medium", creditCents: 1500, label: { usd: "$15", eur: "€15", sek: "150 SEK" }, unitAmount: { usd: 1500, eur: 1500, sek: 15000 } },
  { tier: "large",  creditCents: 5000, label: { usd: "$50", eur: "€50", sek: "500 SEK" }, unitAmount: { usd: 5000, eur: 5000, sek: 50000 } },
];

export function topupOptionForTier(tier: string): TopupOption | undefined {
  return TOPUP_TIERS.find((t) => t.tier === tier);
}
